/* Bronson cart: slide-out drawer, "Added to cart" notification, quantity / remove / note updates */
(() => {
  if (window.BronsonCart) return;

  const SECTIONS = ['bronson-cart-drawer', 'bronson-cart-notification'];
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const HIDE_DELAY = 550;

  const routes = () => window.routes || {};
  const drawerEl = () => document.querySelector('bronson-cart-drawer');
  const notificationEl = () => document.querySelector('bronson-cart-notification');
  const lockScroll = (on) => document.documentElement.classList.toggle('bh-scroll-lock', on);

  const parse = (html, selector) => {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  };

  const updateCount = (count) => {
    const n = parseInt(count, 10);
    if (Number.isNaN(n)) return;
    document.querySelectorAll('.bh__cart-count').forEach((node) => (node.textContent = n));
  };

  const postJSON = async (url, payload) => {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      return { ok: res.ok && !json.status, json };
    } catch (e) {
      return { ok: false, json: {} };
    }
  };

  /* ---------- Drawer ---------- */
  class BronsonCartDrawer extends HTMLElement {
    connectedCallback() {
      // live outside the sticky header so the fixed overlay covers the page
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
        return;
      }
      if (this.bound) return;
      this.bound = true;
      this.target = this.querySelector('[data-bc-target]');
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') this.trapFocus(e);
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(trigger) {
      notificationEl()?.hide();
      if (this.isOpen) return;
      this.trigger = trigger || document.activeElement;
      clearTimeout(this.hideTimer);
      this.hidden = false;
      void this.offsetWidth;
      this.classList.add('is-open');
      lockScroll(true);
      setTimeout(() => this.querySelector('.bc__close')?.focus({ preventScroll: true }), 60);
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      lockScroll(false);
      this.hideTimer = setTimeout(() => (this.hidden = true), HIDE_DELAY);
      if (this.trigger && document.contains(this.trigger)) this.trigger.focus({ preventScroll: true });
    }

    render(html) {
      const next = parse(html, '[data-bc-content]');
      if (!next) return;
      const scroller = this.querySelector('[data-bc-scroll]');
      const scrollTop = scroller ? scroller.scrollTop : 0;
      const focusedKey = document.activeElement?.closest?.('[data-bc-item]')?.dataset.key;
      this.target.replaceChildren(next);
      const nextScroller = this.querySelector('[data-bc-scroll]');
      if (nextScroller) nextScroller.scrollTop = scrollTop;
      if (focusedKey && this.isOpen) this.querySelector(`[data-bc-item][data-key="${CSS.escape(focusedKey)}"] [data-bc-qty]`)?.focus({ preventScroll: true });
      updateCount(next.dataset.count);
    }

    onClick(e) {
      if (e.target.closest('[data-bc-close]')) {
        this.close();
        return;
      }

      const cont = e.target.closest('[data-bc-continue]');
      if (cont) {
        const href = new URL(cont.href, window.location.origin);
        if (href.pathname === window.location.pathname) e.preventDefault();
        this.close();
        return;
      }

      const remove = e.target.closest('[data-bc-remove]');
      if (remove) {
        this.change(remove.dataset.key, 0, remove.closest('[data-bc-item]'));
        return;
      }

      const step = e.target.closest('[data-bc-step]');
      if (step) {
        const input = step.parentElement.querySelector('[data-bc-qty]');
        const next = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(step.dataset.bcStep, 10));
        const item = step.closest('[data-bc-item]');
        if (input.max && next > parseInt(input.max, 10)) {
          this.showError(item, `Only ${input.max} available.`);
          return;
        }
        input.value = next;
        this.change(input.dataset.key, next, item);
        return;
      }

      const toggle = e.target.closest('[data-bc-notes-toggle]');
      if (toggle) {
        const note = this.querySelector('[data-bc-note]');
        const open = note.hidden;
        note.hidden = !open;
        toggle.setAttribute('aria-expanded', String(open));
        if (open) note.focus();
      }
    }

    onChange(e) {
      const input = e.target.closest('[data-bc-qty]');
      if (input) {
        const qty = Math.max(0, parseInt(input.value, 10) || 0);
        this.change(input.dataset.key, qty, input.closest('[data-bc-item]'));
        return;
      }
      const note = e.target.closest('[data-bc-note]');
      if (note) postJSON(`${routes().cart_update_url || '/cart/update'}.js`, { note: note.value });
    }

    showError(item, message) {
      const error = item?.querySelector('[data-bc-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    }

    async change(key, quantity, item) {
      if (!key) return;
      item?.classList.add('is-loading');
      const { ok, json } = await postJSON(`${routes().cart_change_url || '/cart/change'}.js`, {
        id: key,
        quantity,
        sections: SECTIONS,
        sections_url: window.location.pathname,
      });
      if (!ok) {
        item?.classList.remove('is-loading');
        this.showError(item, json.description || json.message || 'Something went wrong. Please try again.');
        return;
      }
      window.BronsonCart.apply(json);
      document.dispatchEvent(new CustomEvent('bronson:cart:change', { detail: json }));
    }

    trapFocus(e) {
      const items = [...this.querySelectorAll(FOCUSABLE)].filter((n) => n.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* ---------- Notification ---------- */
  class BronsonCartNotification extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.target = this.querySelector('[data-bcn-target]');
      this.querySelector('[data-bcn-close]')?.addEventListener('click', () => this.hide());
      this.addEventListener('mouseenter', () => clearTimeout(this.timer));
      this.addEventListener('mouseleave', () => this.isVisible && this.schedule());
      this.addEventListener('focusin', () => clearTimeout(this.timer));
      this.addEventListener('click', (e) => {
        const open = e.target.closest('[data-bc-open]');
        if (!open || !drawerEl()) return;
        e.preventDefault();
        this.hide();
        drawerEl().open(open);
      });
    }

    get isVisible() {
      return this.classList.contains('is-visible');
    }

    get enabled() {
      return this.dataset.enabled !== 'false';
    }

    show(html, keys) {
      const next = parse(html, '[data-bcn-content]');
      if (!next) return false;
      const rows = [...next.querySelectorAll('[data-key]')];
      let shown = 0;
      rows.forEach((row) => {
        row.hidden = !keys.includes(row.dataset.key);
        if (!row.hidden) shown++;
      });
      if (!shown && rows[0]) rows[0].hidden = false;
      this.target.replaceChildren(next);
      clearTimeout(this.hideTimer);
      this.hidden = false;
      void this.offsetWidth;
      this.classList.add('is-visible');
      this.schedule();
      return true;
    }

    schedule() {
      clearTimeout(this.timer);
      const seconds = parseFloat(this.dataset.duration) || 5;
      this.timer = setTimeout(() => this.hide(), seconds * 1000);
    }

    hide() {
      clearTimeout(this.timer);
      if (!this.isVisible) return;
      this.classList.remove('is-visible');
      this.hideTimer = setTimeout(() => (this.hidden = true), HIDE_DELAY);
    }
  }

  if (!customElements.get('bronson-cart-drawer')) customElements.define('bronson-cart-drawer', BronsonCartDrawer);
  if (!customElements.get('bronson-cart-notification')) customElements.define('bronson-cart-notification', BronsonCartNotification);

  /* ---------- Public API ---------- */
  window.BronsonCart = {
    sections: SECTIONS,

    apply(json) {
      const sections = (json && json.sections) || {};
      if (sections['bronson-cart-drawer']) drawerEl()?.render(sections['bronson-cart-drawer']);
      if (json && json.item_count != null) updateCount(json.item_count);
      if (sections['bronson-cart-notification']) this.notificationHtml = sections['bronson-cart-notification'];
    },

    /* call with the /cart/add.js response (requested with `sections`) */
    added(json, trigger) {
      this.apply(json);
      const lines = json.items ? json.items : [json];
      const keys = lines.map((line) => line.key).filter(Boolean);
      const notification = notificationEl();
      if (notification && notification.enabled && this.notificationHtml && notification.show(this.notificationHtml, keys)) return;
      this.open(trigger);
    },

    open(trigger) {
      const drawer = drawerEl();
      if (drawer) drawer.open(trigger);
      else window.location.href = routes().cart_url || '/cart';
    },

    close() {
      drawerEl()?.close();
    },

    async refresh() {
      try {
        const res = await fetch(`${window.location.pathname}?sections=${SECTIONS.join(',')}`);
        if (!res.ok) return;
        this.apply({ sections: await res.json() });
      } catch (e) {}
    },
  };

  // header cart icon (and any [data-bc-open] link) opens the drawer
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('#cart-icon-bubble, [data-bc-open]');
    if (!trigger || trigger.closest('bronson-cart-notification') || !drawerEl()) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    window.BronsonCart.open(trigger);
  });

  // keep in sync with other cart updates (Dawn product forms) and back/forward cache
  if (typeof window.subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    window.subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === 'bronson-quick-view') return;
      window.BronsonCart.refresh();
    });
  }
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) window.BronsonCart.refresh();
  });
})();
